import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { DayPicker } from "@/components/day-picker";
import { type RoadmapItem, useSetRoadmapEffort, useUpdateRoadmapItem } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { shiftMonth } from "@/lib/calendar";
import { effortLabel } from "@/lib/effort";
import { formatDay, formatMonth } from "@/lib/format";

import { DetailsCard, EditPencil, Fact, Failure, FormRow, useFocusBack } from "./details-parts";
import { EffortStepper } from "./effort-stepper";
import { MonthSelect } from "./month-select";
import { StepButtons } from "./step-buttons";
import {
  currentMonth,
  type DeveloperDraft,
  developerDraft,
  developerUpdate,
  itemDraft,
  itemEffort,
  itemUpdate,
  monthOptions,
  PLANNING_FIELDS,
} from "./utils";

/** How far back and ahead a developer can move an item's month. */
const MONTHS_BACK = 12;
const MONTHS_SHOWN = 25;

interface PlanningCardProps {
  item: RoadmapItem;
  pastMonth: boolean;
  /** The viewer sees the hours (an owner and a developer): the effort shows. An admin gets none. */
  showHours: boolean;
  editing: boolean;
  onEditing: (on: boolean) => void;
}

/**
 * The effort, the month and the deadline; the pencil edits what the viewer
 * may. An admin, who sees no hours, gets the month and the deadline alone.
 */
export function PlanningCard({
  item,
  pastMonth,
  showHours,
  editing,
  onEditing,
}: PlanningCardProps) {
  const { t } = useTranslation();
  const pencil = useFocusBack(editing);
  const effort = itemEffort(item);
  const rights = planningRights(item, pastMonth);
  const canEdit = rights.effort || rights.month || rights.deadline;

  return (
    <DetailsCard
      title={t("roadmap.details.planning")}
      section="planning"
      pencil={
        canEdit &&
        !editing && (
          <EditPencil
            ref={pencil}
            label={t("roadmap.details.editPlanning")}
            onClick={() => onEditing(true)}
          />
        )
      }
    >
      {editing ? (
        <PlanningForm
          item={item}
          rights={rights}
          showHours={showHours}
          onClose={() => onEditing(false)}
        />
      ) : (
        <dl className="space-y-1.5 text-sm">
          {showHours && (
            <Fact label={t("roadmap.details.effort")} name="effort">
              {effort.hours === null ? (
                <span className="text-muted">{t("roadmap.effort.unknown")}</span>
              ) : (
                <>
                  {effortLabel(effort.hours)}{" "}
                  <span className="text-muted">
                    ({effort.actual ? t("roadmap.effort.actual") : t("roadmap.effort.estimated")})
                  </span>
                </>
              )}
            </Fact>
          )}
          <Fact label={t("roadmap.details.month")} name="month">
            {item.month ? (
              <span className="first-letter:uppercase">{formatMonth(item.month)}</span>
            ) : (
              <span className="text-muted">{t("roadmap.details.notPlanned")}</span>
            )}
          </Fact>
          <Fact label={t("roadmap.details.deadline")} name="deadline">
            {item.deadline ? (
              <span className="tabular-nums">{formatDay(item.deadline)}</span>
            ) : (
              <span className="text-muted">{t("roadmap.details.none")}</span>
            )}
          </Fact>
        </dl>
      )}
    </DetailsCard>
  );
}

/** What the viewer may change in Planning. */
interface PlanningRights {
  effort: boolean;
  month: boolean;
  deadline: boolean;
}

/**
 * What the viewer may change in Planning: the effort of an item not done and
 * the month of an item that is no idea as a developer (`set_effort`, also
 * from a past month), the deadline with `edit_text`, never from a past month.
 */
function planningRights(item: RoadmapItem, pastMonth: boolean): PlanningRights {
  const developer = Boolean(item.developer) && item.can.set_effort;
  return {
    effort: developer && item.status !== "done",
    month: developer && item.status !== "idea",
    deadline: item.can.edit_text && !pastMonth,
  };
}

interface PlanningFormProps {
  item: RoadmapItem;
  rights: PlanningRights;
  /** The viewer sees the hours: the effort shows, read-only where they may not change it. */
  showHours: boolean;
  onClose: () => void;
}

/**
 * Planning in place: the effort as a stepper, the month, the deadline. Save
 * sends only what the viewer changed here: the effort and the month through
 * the developer's route, the deadline through the item's. A field left alone
 * follows the item, so a change saved meanwhile (the Linear tickets in the
 * Ontwikkelaar card, the month Markeer klaar sets) is never sent back.
 */
function PlanningForm({ item, rights, showHours, onClose }: PlanningFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const effortSave = useSetRoadmapEffort();
  const textSave = useUpdateRoadmapItem();
  // Only what the viewer changed here; the rest follows the item as it is now.
  const [edits, setEdits] = useState<Partial<DeveloperDraft>>({});
  const [deadlineEdit, setDeadlineEdit] = useState<{ day: string | null } | null>(null);
  const draft = developerDraft(item, edits);
  const deadline = deadlineEdit ? deadlineEdit.day : (item.deadline ?? null);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{
    error: unknown;
    action: "setRoadmapEffort" | "updateRoadmapItem";
  } | null>(null);
  const pending = effortSave.isPending || textSave.isPending;

  const developer =
    rights.effort || rights.month ? developerUpdate(item, draft, PLANNING_FIELDS) : null;
  const text = rights.deadline
    ? itemUpdate(item, { ...itemDraft(item), deadline }, { edit_text: true, edit_priority: false })
    : null;
  const changed = Boolean(developer?.change || text);

  const save = async () => {
    if (!changed || developer?.problem || pending) {
      return;
    }
    setFailure(null);
    try {
      if (developer?.change) {
        await effortSave.mutateAsync({ itemId: item.id, change: developer.change });
      }
    } catch (error) {
      setFailure(inlineFailure({ error, action: "setRoadmapEffort" }));
      return;
    }
    try {
      if (text) {
        await textSave.mutateAsync({ itemId: item.id, change: text });
      }
    } catch (error) {
      setFailure(inlineFailure({ error, action: "updateRoadmapItem" }));
      return;
    }
    onClose();
  };

  const now = currentMonth();
  return (
    <form
      className="space-y-3"
      data-section="planning-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {(rights.effort || showHours) && (
        <FormRow label={t("roadmap.details.effort")}>
          {rights.effort ? (
            <EffortStepper
              value={draft.effort}
              onChange={(effort) => setEdits({ ...edits, effort })}
              allowNone={item.status === "idea"}
              label={t("roadmap.details.effort")}
              autoFocus
            />
          ) : (
            <p className="text-sm">{effortLabel(itemEffort(item).hours)}</p>
          )}
        </FormRow>
      )}
      <FormRow
        label={t("roadmap.details.month")}
        htmlFor={rights.month ? `${ids}-month` : undefined}
      >
        {rights.month && draft.month ? (
          <MonthSelect
            id={`${ids}-month`}
            value={draft.month}
            onChange={(month) => setEdits({ ...edits, month })}
            months={monthOptions(shiftMonth(now, -MONTHS_BACK), MONTHS_SHOWN, draft.month)}
            label={t("roadmap.details.month")}
            // The pencil that opened the form is gone: its first control takes the focus.
            autoFocus={!rights.effort}
          />
        ) : (
          <p className="text-muted text-sm">
            {item.status === "idea"
              ? t("roadmap.details.monthAtPlan")
              : item.month
                ? formatMonth(item.month)
                : t("roadmap.details.none")}
          </p>
        )}
      </FormRow>
      <FormRow
        label={t("roadmap.details.deadline")}
        htmlFor={rights.deadline ? `${ids}-deadline` : undefined}
      >
        {rights.deadline ? (
          <DayPicker
            id={`${ids}-deadline`}
            value={deadline}
            onChange={(day) => setDeadlineEdit({ day })}
            label={t("roadmap.details.deadline")}
            placeholder={t("roadmap.form.noDeadline")}
            autoFocus={!rights.effort && !(rights.month && draft.month)}
          />
        ) : (
          <p className="text-sm tabular-nums">
            {item.deadline ? formatDay(item.deadline) : t("roadmap.details.none")}
          </p>
        )}
      </FormRow>
      {developer?.problem && <Failure>{t(developer.problem)}</Failure>}
      {failure && <Failure>{apiErrorMessage(failure.error, failure.action)}</Failure>}
      <StepButtons
        onBack={onClose}
        confirm={t("common.save")}
        pending={pending}
        disabled={!changed || Boolean(developer?.problem)}
      />
    </form>
  );
}
